import HgModal from "@hg/modal"
import cls from "classnames"
import { AnimatePresence, motion } from "motion/react"
import { useCallback } from "react"
import { createPortal } from "react-dom"

import { i18n } from "@/i18n"
import StrokeText from "../StrokeText"

import styles from "./index.module.scss"

interface CloseButtonProps {
    onClick?: () => void
    className?: string
}

const CloseButton: React.FC<CloseButtonProps> = (props) => {
    const { onClick, className } = props

    return (
        <button
            className={cls(styles.close, className)}
            type="button"
            aria-label="close"
            onClick={onClick}
        />
    )
}

interface ActionsProps {
    showCancelBtn?: boolean
    cancelText?: string
    cancelButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
    onCancel?: () => void
    showOkBtn?: boolean
    okText?: string
    okButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
    onOk?: () => void
    actionsClassName?: string
    btnFontSize?: number
}

const Actions: React.FC<ActionsProps> = (props) => {
    const {
        okText = i18n.get("common.ok"),
        onOk,
        showOkBtn = true,
        cancelText = i18n.get("common.cancel"),
        onCancel,
        showCancelBtn = true,
        cancelButtonProps,
        okButtonProps,
        actionsClassName,
        btnFontSize = 40,
    } = props

    return (
        <div className={cls(styles.actions, actionsClassName)}>
            {showCancelBtn && (
                <button
                    className={styles.cancel}
                    type="button"
                    aria-label="cancel"
                    onClick={onCancel}
                    {...cancelButtonProps}
                >
                    <StrokeText
                        className={styles.btnText}
                        text={cancelText}
                        fontFamily="source-han-sans-japanese"
                        fontWeight={700}
                        fontSize={btnFontSize}
                        fillColor="#fff"
                        borderWidth={4}
                        borderColor="#564426"
                        offsetY={0}
                        padding={4}
                        baselineAligned
                    />
                </button>
            )}
            {showOkBtn && (
                <button
                    className={styles.ok}
                    type="button"
                    aria-label="ok"
                    onClick={onOk}
                    {...okButtonProps}
                >
                    <StrokeText
                        className={styles.btnText}
                        text={okText}
                        fontFamily="source-han-sans-japanese"
                        fontWeight={700}
                        fontSize={btnFontSize}
                        fillColor="#fff"
                        borderWidth={4}
                        borderColor="#564426"
                        offsetY={0}
                        padding={4}
                        baselineAligned
                    />
                </button>
            )}
        </div>
    )
}

interface ModalContentProps extends ActionsProps {
    children?: React.ReactNode
    size?: "small" | "medium" | "large" | "event" | "gallery" | "letter"
    showClose?: boolean
    contentClassName?: string
    closeButtonClassName?: string
}

export const ModalContent: React.FC<ModalContentProps> = (props) => {
    const {
        okText = i18n.get("common.ok"),
        onOk,
        showOkBtn = true,
        cancelText = i18n.get("common.cancel"),
        onCancel,
        showCancelBtn = true,
        cancelButtonProps,
        okButtonProps,
        size = "small",
        showClose = true,
        contentClassName,
        closeButtonClassName,
        actionsClassName,
        btnFontSize,
        children,
    } = props

    return (
        <motion.div
            className={cls(styles.content, styles[size], contentClassName)}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            onClick={(e) => e.stopPropagation()}
        >
            {children}
            {showClose && (
                <CloseButton
                    onClick={onCancel}
                    className={closeButtonClassName}
                />
            )}
            <Actions
                showCancelBtn={showCancelBtn}
                cancelText={cancelText}
                cancelButtonProps={cancelButtonProps}
                onCancel={onCancel}
                showOkBtn={showOkBtn}
                okText={okText}
                okButtonProps={okButtonProps}
                onOk={onOk}
                actionsClassName={actionsClassName}
                btnFontSize={btnFontSize}
            />
        </motion.div>
    )
}

interface Props extends ModalContentProps {
    open?: boolean
    maskClosable?: boolean
}

export const Modal: React.FC<Props> = (props) => {
    const { open, maskClosable, ...contentProps } = props

    return (
        <HgModal
            active={open}
            maskClosable={maskClosable}
            onClose={contentProps.onCancel}
        >
            <ModalContent {...contentProps} />
        </HgModal>
    )
}

export const CustomModal: React.FC<Props> = (props) => {
    const { open, maskClosable, ...contentProps } = props

    const handleMaskClick = useCallback(() => {
        if (maskClosable) {
            contentProps.onCancel?.()
        }
    }, [maskClosable, contentProps.onCancel])

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div
                    className={styles.mask}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    onClick={handleMaskClick}
                >
                    <ModalContent {...contentProps} />
                </motion.div>
            )}
        </AnimatePresence>,
        document.body,
    )
}
